<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Pending Orders</h1>
            <p class="text-orange-100 text-lg">Manage and track all customer orders</p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="refreshOrders"
              :disabled="loading"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-6 mb-8">
      <div class="border-b border-gray-200">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="selectTab(tab.value)"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
              activeTab === tab.value
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <div class="flex items-center space-x-2">
              <span>{{ tab.label }}</span>
              <span
                v-if="tab.count !== undefined"
                :class="[
                  'px-2 py-1 text-xs rounded-full',
                  activeTab === tab.value
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-100 text-gray-600'
                ]"
              >
                {{ tab.count }}
              </span>
            </div>
          </button>
        </nav>
      </div>
      

    </div>

    <!-- Orders Table -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
      <div class="px-4 py-5 sm:p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading orders...</p>
        </div>
        
        <div v-else-if="orders.length === 0" class="text-center py-8">
          <div class="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
          <p class="text-gray-500">Try adjusting your filters or check back later.</p>
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="order in orders"
            :key="order.id"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex justify-between items-start mb-3">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">
                  Order #{{ order.order_no }}
                </h3>
                <p class="text-sm text-gray-600">
                  {{ order.customer_name }} • {{ order.customer_phone }}
                </p>
              </div>
              <div class="text-right">
                <span
                  :class="getStatusClass(order.status, order)"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ formatStatus(order.status, order) }}
                </span>
                <p class="text-sm text-gray-500 mt-1">
                  {{ parseFloat(order.total_gross_weight || 0).toFixed(0) }}g gross
                </p>
                <p class="text-sm text-gray-500">
                  {{ parseFloat(order.total_net_weight || 0).toFixed(0) }}g net
                </p>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Products:</span> {{ order.product_count || 0 }} {{ order.product_count === 1 ? 'product' : 'products' }}, {{ order.item_count || 0 }} {{ order.item_count === 1 ? 'item' : 'items' }}
                </p>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Created:</span> {{ formatDate(order.created_at) }}
                </p>
              </div>
              <div>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Workshop:</span> {{ order.workshop_user_name || 'Not assigned' }}
                </p>
                <p v-if="order.due_date" class="text-sm text-gray-600">
                  <span class="font-medium">Due:</span> {{ formatDate(order.due_date) }}
                </p>
                <div v-if="order.status === 'cancelled' && order.cancelled_by_name" class="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p class="text-sm text-red-700">
                    <span class="font-medium">Cancelled by:</span> {{ order.cancelled_by_name }}
                  </p>
                  <p class="text-xs text-red-600">
                    <span class="font-medium">On:</span> {{ formatDate(order.cancelled_at) }}
                  </p>
                </div>
              </div>
            </div>
            
            <div class="flex space-x-2">
              <button
                @click="viewOrderDetails(order.id)"
                class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
              >
                View Details
              </button>

              <LoadingButton
                v-if="order.status === 'pending' && !order.assigned_workshop_user"
                @click="updateOrderStatus(order.id, 'advance_paid')"
                :loading="orderLoadingStates[order.id]"
                text="Mark Advance Paid"
                loading-text="Processing..."
                variant="success"
                size="sm"
              />
              <button
                v-if="order.status === 'advance_paid'"
                @click="openWorkshopAssignmentModal(order)"
                class="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors duration-200"
              >
                Send to Workshop (Pending)
              </button>
              <LoadingButton
                v-if="order.status === 'completed'"
                @click="updateOrderStatus(order.id, 'delivered')"
                :loading="orderLoadingStates[order.id]"
                text="Mark Delivered"
                loading-text="Processing..."
                variant="info"
                size="sm"
              />
              <button
                v-if="order.status !== 'cancelled' && order.status !== 'delivered' && order.status !== 'completed' && !order.assigned_workshop_user"
                @click="cancelOrder(order.id)"
                :disabled="orderLoadingStates[order.id]"
                class="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
              >
                {{ orderLoadingStates[order.id] ? 'Cancelling...' : 'Cancel Order' }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Pagination -->
        <div v-if="pagination.totalPages > 1" class="mt-6 flex justify-center">
          <nav class="flex space-x-2">
            <button
              @click="changePage(pagination.page - 1)"
              :disabled="pagination.page <= 1"
              class="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
            >
              Previous
            </button>
            <span class="px-3 py-2 text-sm text-gray-700">
              Page {{ pagination.page }} of {{ pagination.totalPages }}
            </span>
            <button
              @click="changePage(pagination.page + 1)"
              :disabled="pagination.page >= pagination.totalPages"
              class="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Workshop Assignment Modal -->
    <div v-if="showWorkshopModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Send to Workshop (Pending)</h3>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Workshop User</label>
            <select
              v-model="selectedWorkshopUser"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select workshop user</option>
              <option v-for="user in workshopUsers" :key="user.id" :value="user.id">
                {{ user.name }}
              </option>
            </select>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
            <input
              v-model="selectedDueDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div class="flex justify-end space-x-3">
            <button
              @click="closeWorkshopModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <LoadingButton
              @click="assignToWorkshop"
              :disabled="!selectedWorkshopUser || !selectedDueDate"
              :loading="workshopModalLoading"
              text="Send to Pending"
              loading-text="Assigning..."
              variant="primary"
              size="sm"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Order Details Modal -->
    <div v-if="showDetailsModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">Order Details</h3>
            <button
              @click="closeDetailsModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div v-if="selectedOrderDetails" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 class="text-lg font-semibold">Order #{{ selectedOrderDetails.order_no }}</h4>
                <p class="text-sm text-gray-600">Customer: {{ selectedOrderDetails.customer_name }}</p>
                <p class="text-sm text-gray-600">Phone: {{ selectedOrderDetails.customer_phone }}</p>
                <p class="text-sm text-gray-600">Email: {{ selectedOrderDetails.customer_email }}</p>
              </div>
              <div class="text-right">
                <span :class="getStatusClass(selectedOrderDetails.status, selectedOrderDetails)" class="px-2 py-1 text-xs font-medium rounded-full">
                  {{ formatStatus(selectedOrderDetails.status, selectedOrderDetails) }}
                </span>
                <p class="text-sm text-gray-500 mt-1">{{ getTotalGrossWeight(selectedOrderDetails.items) }}g gross</p>
                <p class="text-sm text-gray-500">{{ getTotalNetWeight(selectedOrderDetails.items) }}g net</p>
                <p class="text-sm text-gray-500 mt-1">Created: {{ formatDate(selectedOrderDetails.created_at) }}</p>
              </div>
            </div>
            <!-- Combined Totals Section -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 class="font-semibold text-blue-900 mb-3">Order Summary</h4>
              <div class="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p class="text-sm text-blue-600">Total Quantity</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalQuantity(selectedOrderDetails.items) }}</p>
                </div>
                <div>
                  <p class="text-sm text-blue-600">Total Gross Weight</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalGrossWeight(selectedOrderDetails.items) }}g</p>
                </div>
                <div>
                  <p class="text-sm text-blue-600">Total Net Weight</p>
                  <p class="text-lg font-bold text-blue-900">{{ getTotalNetWeight(selectedOrderDetails.items) }}g</p>
                </div>
              </div>
            </div>

            <div>
              <h5 class="font-medium text-gray-900 mb-2">Items</h5>
              <div class="space-y-2">
                <div v-for="item in selectedOrderDetails.items" :key="item.id" class="border border-gray-200 rounded p-3">
                  <div class="flex gap-4">
                    <!-- Product Images -->
                    <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                      <div class="relative group cursor-pointer" @click="viewImageModal(item.images, 0, item.name)" title="Click to view full size">
                        <img
                          :src="getImageUrl(item.images[0])"
                          :alt="item.name"
                          class="w-20 h-20 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                          @error="handleImageError"
                        />
                        <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          +{{ item.images.length - 1 }}
                        </div>
                        <!-- Hover overlay -->
                        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg flex items-center justify-center transition-all duration-200">
                          <svg class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Product Details -->
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <p class="font-medium">{{ item.name }}</p>
                          <p class="text-sm text-gray-600">SKU: {{ item.sku }}</p>
                          <p class="text-sm text-gray-600">Category: {{ item.category }}</p>
                          <p class="text-sm text-gray-600">Metal: {{ item.metal }}</p>
                          <div class="mt-2 space-y-1">
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Gross Weight:</span> {{ item.gross_weight || 0 }}g
                            </p>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Net Weight:</span> {{ item.net_weight || 0 }}g
                            </p>
                          </div>
                        </div>
                        <div class="text-right">
                          <p class="text-sm font-medium text-gray-900">Qty: {{ item.quantity || item.qty }}</p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Gross Total:</span> {{ getItemGrossTotal(item) }}g
                          </p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Net Total:</span> {{ getItemNetTotal(item) }}g
                          </p>
                        </div>
                      </div>
                      
                      <!-- Additional Images Gallery -->
                      <div v-if="item.images && item.images.length > 1" class="mt-2">
                        <div class="flex gap-2 overflow-x-auto">
                          <img
                            v-for="(image, index) in item.images.slice(1, 4)"
                            :key="index"
                            :src="getImageUrl(image)"
                            :alt="`${item.name} - Image ${index + 2}`"
                            class="w-12 h-12 object-contain rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                            @click="viewImageModal(item.images, index + 1, item.name)"
                            @error="handleImageError"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="selectedOrderDetails.history && selectedOrderDetails.history.length > 0">
              <h5 class="font-medium text-gray-900 mb-2">Status History</h5>
              <div class="space-y-2">
                <div v-for="history in selectedOrderDetails.history" :key="history.id" class="border-l-4 border-primary-500 pl-3 py-2">
                  <p class="text-sm font-medium">{{ history.new_status }}</p>
                  <p class="text-xs text-gray-600">{{ formatDate(history.created_at) }}</p>
                  <p v-if="history.notes" class="text-xs text-gray-600">{{ history.notes }}</p>
                  <p v-if="history.updated_by_name" class="text-xs text-gray-500">By: {{ history.updated_by_name }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-gray-500">Loading order details...</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Image Modal -->
  <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black bg-opacity-75" @click="closeImageModal"></div>
    <div class="relative bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">{{ currentProductName || (currentImages.length > 0 ? 'Product Images' : 'No Images Available') }}</h3>
        <button @click="closeImageModal" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- Modal Content -->
      <div class="p-4">
        <div class="relative">
          <!-- Main Image -->
          <div class="flex justify-center items-center bg-gray-100 rounded-lg overflow-hidden" style="min-height: 400px;">
            <img
              v-if="currentImages.length > 0"
              :src="getImageUrl(currentImages[currentImageIndex])"
              :alt="`Product Image ${currentImageIndex + 1}`"
              class="max-w-full max-h-96 object-contain"
              @error="handleImageError"
            />
            <div v-else class="text-gray-500 text-center">
              <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>No images available</p>
            </div>
          </div>
          
          <!-- Navigation Buttons -->
          <button
            v-if="currentImages.length > 1"
            @click="previousImage"
            class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
          >
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            v-if="currentImages.length > 1"
            @click="nextImage"
            class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
          >
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <!-- Thumbnail Navigation -->
        <div v-if="currentImages.length > 1" class="mt-4">
          <div class="flex justify-center gap-2 overflow-x-auto">
            <button
              v-for="(image, index) in currentImages"
              :key="index"
              @click="setCurrentImage(index)"
              :class="[
                'flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all duration-200',
                currentImageIndex === index
                  ? 'border-orange-500 shadow-lg'
                  : 'border-gray-300 hover:border-gray-400'
              ]"
            >
              <img
                :src="getImageUrl(image)"
                :alt="`Thumbnail ${index + 1}`"
                class="w-full h-full object-cover"
                @error="handleImageError"
              />
            </button>
          </div>
        </div>
        
        <!-- Image Counter -->
        <div v-if="currentImages.length > 1" class="mt-2 text-center text-sm text-gray-600">
          {{ currentImageIndex + 1 }} of {{ currentImages.length }}
        </div>
      </div>
    </div>
    
  </div>
  
  <!-- Cancel Order Confirmation Modal -->
  <ConfirmDialog
    :visible="showCancelConfirm"
    title="Cancel Order"
    message="Are you sure you want to cancel this order? This action cannot be undone."
    confirm-text="Yes, Cancel Order"
    cancel-text="Keep Order"
    @confirm="confirmCancelOrder"
    @cancel="cancelCancelOrder"
  />
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ordersAPI, adminAPI } from '@/services/api'
import LoadingButton from '@/components/LoadingButton.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useOrderStatusesStore } from '@/stores/orderStatuses'
import { useNotificationStore } from '@/stores/notification'
import { PAGINATION_LIMITS, createPagination } from '@/constants/pagination'

export default {
  name: 'PendingOrders',
  components: {
    LoadingButton,
    ConfirmDialog
  },
  setup() {
    const orders = ref([])
    const loading = ref(false)
    const workshopUsers = ref([])
    const showWorkshopModal = ref(false)
    const selectedOrder = ref(null)
    const selectedWorkshopUser = ref('')
    const selectedDueDate = ref('')
    const showDetailsModal = ref(false)
    const selectedOrderDetails = ref(null)
    
    // Cancel order confirmation state
    const showCancelConfirm = ref(false)
    const orderToCancel = ref(null)
    
    
    // Loading states for individual order actions
    const orderLoadingStates = ref({})
    const workshopModalLoading = ref(false)
    
    // Order statuses store
    const orderStatusesStore = useOrderStatusesStore()
    
    // Notification store
    const notificationStore = useNotificationStore()
    const pagination = ref(createPagination(PAGINATION_LIMITS.ORDERS))
    
    // Image modal variables
    const showImageModal = ref(false)
    const currentImages = ref([])
    const currentImageIndex = ref(0)
    const currentProductName = ref('')
    
    // Tab functionality
    const activeTab = ref('all')
    
    const tabs = ref([
      { label: 'All', value: 'all', count: 0 },
      { label: 'Pending', value: 'pending', count: 0 },
      { label: 'Advance Paid', value: 'advance_paid', count: 0 },
      { label: 'Pending in Workshop', value: 'pending_in_workshop', count: 0 },
      { label: 'In Workshop', value: 'in_workshop', count: 0 },
      { label: 'Completed', value: 'completed', count: 0 },
      { label: 'Delivered', value: 'delivered', count: 0 },
      { label: 'Cancelled', value: 'cancelled', count: 0 }
    ])
    
    const selectTab = (tabValue) => {
      activeTab.value = tabValue
      pagination.value.page = 1
      loadOrders()
    }
    

    
    const loadOrders = async () => {
      try {
        loading.value = true
        
        let params = {
          page: pagination.value.page,
          limit: pagination.value.limit
        }
        
        // Handle special case for "pending_in_workshop"
        if (activeTab.value === 'pending_in_workshop') {
          params.status = 'pending'
          params.assigned_workshop = true
        } else if (activeTab.value !== 'all') {
          params.status = activeTab.value
        }
        // For 'all' tab, don't add status filter
        
        const response = await adminAPI.getOrders(params)
        
        console.log('Orders response:', response.data.orders)
        console.log('First order details:', response.data.orders[0])
        
        // Filter orders for "pending_in_workshop" tab
        if (activeTab.value === 'pending_in_workshop') {
          orders.value = response.data.orders.filter(order => 
            order.status === 'pending' && order.assigned_workshop_user
          )
        } else {
          orders.value = response.data.orders
        }
        
        console.log('Filtered orders:', orders.value)
        
        // Debug which orders should show cancel button
        orders.value.forEach(order => {
          const shouldShowCancel = order.status !== 'cancelled' && 
                                  order.status !== 'delivered' && 
                                  order.status !== 'completed' && 
                                  !order.assigned_workshop_user
          console.log(`Order ${order.order_no}: status=${order.status}, assigned_workshop_user=${order.assigned_workshop_user}, shouldShowCancel=${shouldShowCancel}`)
        })
        
        pagination.value = response.data.pagination
        
        // Update tab counts if available in response
        if (response.data.statusCounts) {
          // Calculate total count for "all" tab
          const totalCount = Object.values(response.data.statusCounts).reduce((sum, count) => sum + count, 0)
          
          // Create a new array to trigger reactivity
          tabs.value = tabs.value.map(tab => {
            if (tab.value === 'all') {
              return { ...tab, count: totalCount }
            } else {
              return { ...tab, count: response.data.statusCounts[tab.value] || 0 }
            }
          })
        }
      } catch (error) {
        console.error('Failed to load orders:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadWorkshopUsers = async () => {
      try {
        const response = await adminAPI.getUsers({ role: 'workshop', active: true })
        workshopUsers.value = response.data.users
      } catch (error) {
        console.error('Failed to load workshop users:', error)
      }
    }
    
    const refreshOrders = () => {
      pagination.value.page = 1
      loadOrders()
    }
    
    const changePage = (page) => {
      pagination.value.page = page
      loadOrders()
    }
    

    
    const updateOrderStatus = async (orderId, status) => {
      try {
        // Set loading state for this specific order
        orderLoadingStates.value[orderId] = true
        
        const response = await ordersAPI.updateOrderStatus(orderId, status)
        console.log('Order status update response:', response)
        console.log('Order status updated successfully, refreshing list...')
        await loadOrders() // Refresh the list
        console.log('List refreshed, tab counts should be updated')
      } catch (error) {
        console.error('Failed to update order status:', error)
        console.error('Error details:', error.response?.data)
        // You could add a toast notification here for better UX
      } finally {
        // Clear loading state
        orderLoadingStates.value[orderId] = false
      }
    }
    
    const openWorkshopAssignmentModal = async (order) => {
      selectedOrder.value = order
      selectedWorkshopUser.value = ''
      selectedDueDate.value = ''
      showWorkshopModal.value = true
      
      // Refresh workshop users list to include newly created users
      await loadWorkshopUsers()
    }
    
    const closeWorkshopModal = () => {
      showWorkshopModal.value = false
      selectedOrder.value = null
      selectedWorkshopUser.value = ''
      selectedDueDate.value = ''
    }
    
    const assignToWorkshop = async () => {
      try {
        if (!selectedWorkshopUser.value || !selectedDueDate.value) {
          alert('Please select a workshop user and due date')
          return
        }
        
        workshopModalLoading.value = true
        
        // First update status to pending (not in_workshop)
        await ordersAPI.updateOrderStatus(selectedOrder.value.id, 'pending')
        
        // Then assign to workshop user
        await ordersAPI.assignOrder(
          selectedOrder.value.id,
          selectedWorkshopUser.value,
          selectedDueDate.value
        )
        
        closeWorkshopModal()
        await loadOrders() // Refresh the list
      } catch (error) {
        console.error('Failed to assign to workshop:', error)
        
        // Provide more specific error messages
        let errorMessage = 'Failed to assign order to workshop'
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        }
        
        alert(errorMessage)
      } finally {
        workshopModalLoading.value = false
      }
    }

    // Cancel order functions
    const cancelOrder = async (orderId) => {
      orderToCancel.value = orderId
      showCancelConfirm.value = true
    }

    const confirmCancelOrder = async () => {
      if (!orderToCancel.value) return
      
      try {
        // Set loading state for this specific order
        orderLoadingStates.value[orderToCancel.value] = true
        
        await ordersAPI.cancelOrder(orderToCancel.value)
        await loadOrders() // Reload orders
        notificationStore.showSuccess('Order Cancelled', 'Order has been cancelled successfully')
      } catch (err) {
        console.error('Failed to cancel order:', err)
        const errorMessage = err.response?.data?.message || 'Failed to cancel order. Please try again.'
        notificationStore.showError('Cancellation Failed', errorMessage)
      } finally {
        // Clear loading state
        orderLoadingStates.value[orderToCancel.value] = false
        showCancelConfirm.value = false
        orderToCancel.value = null
      }
    }

    const cancelCancelOrder = () => {
      showCancelConfirm.value = false
      orderToCancel.value = null
    }
    
    const viewOrderDetails = async (orderId) => {
      try {
        showDetailsModal.value = true
        selectedOrderDetails.value = null
        const response = await ordersAPI.getOrder(orderId)
        selectedOrderDetails.value = response.data.order
      } catch (error) {
        console.error('Failed to load order details:', error)
        alert('Failed to load order details')
        showDetailsModal.value = false
      }
    }
    
    const closeDetailsModal = () => {
      showDetailsModal.value = false
      selectedOrderDetails.value = null
    }
    

    
    const getStatusClass = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, use workshop styling
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'bg-purple-100 text-purple-800'
      }
      
      // Use dynamic status from store
      return orderStatusesStore.getStatusColorClass(status)
    }
    
    const formatStatus = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show "Pending in Workshop"
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Pending in Workshop'
      }
      
      // Use dynamic status from store
      return orderStatusesStore.getStatusDisplayName(status)
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }
    
    // Image handling functions
    const getImageUrl = (imageName) => {
      if (!imageName) return ''
      
      // If it's already a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const handleImageError = (event) => {
      // Set a fallback image when the actual image fails to load
      event.target.src = 'data:image/svg+xml;base64,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'
    }

    // Image modal functions
    const viewImageModal = (images, startIndex = 0, productName = '') => {
      currentImages.value = images
      currentImageIndex.value = startIndex
      currentProductName.value = productName
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      currentImages.value = []
      currentImageIndex.value = 0
      currentProductName.value = ''
    }

    const nextImage = () => {
      if (currentImageIndex.value < currentImages.value.length - 1) {
        currentImageIndex.value++
      } else {
        currentImageIndex.value = 0
      }
    }

    const previousImage = () => {
      if (currentImageIndex.value > 0) {
        currentImageIndex.value--
      } else {
        currentImageIndex.value = currentImages.value.length - 1
      }
    }

    const setCurrentImage = (index) => {
      currentImageIndex.value = index
    }

    // Helper methods for weight calculations
    const getTotalQuantity = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => total + (item.quantity || item.qty || 0), 0)
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }

    const getItemGrossTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const grossWeight = parseFloat(item.gross_weight) || 0
      return (grossWeight * quantity).toFixed(2)
    }

    const getItemNetTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const netWeight = parseFloat(item.net_weight) || 0
      return (netWeight * quantity).toFixed(2)
    }

    onMounted(async () => {
      await orderStatusesStore.fetchStatuses()
      loadOrders()
      loadWorkshopUsers()
    })
    
    return {
      orders,
      loading,
      workshopUsers,
      showWorkshopModal,
      selectedWorkshopUser,
      selectedDueDate,
      showDetailsModal,
      selectedOrderDetails,
      pagination,
      orderLoadingStates,
      workshopModalLoading,
      loadOrders,
      loadWorkshopUsers,
      refreshOrders,
      changePage,

      updateOrderStatus,
      openWorkshopAssignmentModal,
      closeWorkshopModal,
      assignToWorkshop,
      cancelOrder,
      confirmCancelOrder,
      cancelCancelOrder,
      showCancelConfirm,
      orderToCancel,
      viewOrderDetails,
      closeDetailsModal,

      getStatusClass,
      formatStatus,
      formatDate,
      activeTab,
      selectTab,
      orderStatusesStore,

      // Image modal variables and functions
      showImageModal,
      currentImages,
      currentImageIndex,
      currentProductName,
      getImageUrl,
      handleImageError,
      viewImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      setCurrentImage,

      // Weight calculation methods
      getTotalQuantity,
      getTotalGrossWeight,
      getTotalNetWeight,
      getItemGrossTotal,
      getItemNetTotal,

      tabs
    }
  }
}
</script> 